import { useEffect, useMemo, useRef, useState } from 'react';

import { realtime, type LiveError } from './ws';

export interface LiveState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** When the server last handed this screen a copy. Null until the first one. */
  updatedAt: number | null;
  /** Asks for a fresh copy now rather than waiting for the next beat. */
  reload: () => void;
}

/**
 * Keeps a screen looking at a server-side resource, for as long as it is mounted.
 *
 * The console's tables used to fetch once and then show whatever was true when
 * they loaded — an operator watching the review queue would not see a
 * registration until they navigated away and back. A screen here *subscribes*
 * instead, and the server sends a fresh copy on three occasions: when the
 * subscription opens, whenever any write changes the data behind it, and on the
 * ten-second heartbeat. None of them are distinguishable from in here, which is
 * deliberate — the screen renders the last copy it was given and never has to
 * reason about why it arrived.
 *
 * Shaped like an ordinary fetch hook — `data` / `loading` / `error` / `reload` —
 * so a screen reads the same whether or not it knows where its data came from.
 *
 * @param resource one of the names the server's live registry publishes
 * @param params   filters, paging and sort — the same values the REST route takes
 * @param enabled  false to hold off, for a screen whose key is not ready yet
 */
export function useLive<T>(
  resource: string,
  params: Record<string, unknown> = {},
  { enabled = true }: { enabled?: boolean } = {},
): LiveState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  /*
   * Params are compared by value, not by identity.
   *
   * Callers build this object inline — `useLive('draws', { page, status })` —
   * so it is a new object every render. Keyed on identity, every render would
   * tear the subscription down and open another.
   */
  const key = JSON.stringify(params ?? {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useMemo(() => params ?? {}, [key]);

  // Held in a ref so a re-render caused by incoming data cannot re-subscribe.
  const latest = useRef(0);
  /**
   * The last payload actually handed to the screen, serialised.
   *
   * The heartbeat re-answers every subscription every ten seconds whether or
   * not anything moved, and handing back an equal-but-new object each time made
   * every dependent effect fire again. On a table that is invisible; on a form
   * whose fields are seeded from the data it is not — the operator types, the
   * beat lands, and their half-written text is replaced by what the server
   * still holds. Comparing here means `data` keeps its identity until the data
   * genuinely changes.
   */
  const lastPayload = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const ticket = ++latest.current;
    setLoading(true);
    setError(null);

    // A new query's first answer must always paint, even if it happens to match
    // the previous query's.
    lastPayload.current = null;

    const stop = realtime.watch(
      resource,
      stable,
      (payload) => {
        if (ticket !== latest.current) return;

        const serialised = JSON.stringify(payload);
        if (lastPayload.current === serialised) {
          // Unchanged. Say when it was confirmed, but do not replace the data —
          // see `lastPayload` above.
          setUpdatedAt(Date.now());
          setLoading(false);
          return;
        }
        lastPayload.current = serialised;

        setData(payload as T);
        setUpdatedAt(Date.now());
        setError(null);
        setLoading(false);
      },
      (failure: LiveError) => {
        if (ticket !== latest.current) return;
        setError(failure.message);
        setLoading(false);
      },
    );

    return stop;
  }, [resource, stable, enabled]);

  return {
    data,
    loading,
    error,
    updatedAt,
    reload: () => realtime.syncNow(),
  };
}
